import React from 'react';

// Types for space members
export type SpaceMember = {
    ldbId: string;
    ldb: {
        _id: string;
        fullName?: string;
        firstName?: string;
        email?: string;
        profile?: {
            avatar?: string;
            businessName?: string;
        };
    };
};

export type SpaceGuest = {
    guestId: string;
    guest: {
        _id: string;
        firstName?: string;
        lastName?: string;
        email?: string;
    };
};

/**
 * Parses mentions in message content using actual space member names for accurate highlighting
 * @param content - The message content to parse
 * @param spaceMembers - Array of space members for accurate name matching
 * @returns Array of React nodes with mentions highlighted
 */
export const parseMentions = (
    content: string | null | undefined, 
    spaceMembers: (SpaceMember | SpaceGuest)[] = []
): React.ReactNode[] => {
    if (!content || typeof content !== 'string') {
        return [content || ''];
    }

    const cleanedContent = content
        .replace(/\*\*(.*?)\*\*/g, '$1') 
        .replace(/\* /g, '• '); 

    // Create a list of all possible member names and emails to match against
    const memberIdentifiers: string[] = [];
    
    spaceMembers.forEach(member => {
        // Handle both SpaceMember and SpaceGuest types
        if ('ldb' in member && member.ldb) {
            // SpaceMember with ldb data
            const name = member.ldb.fullName || member.ldb.firstName || "";
            const email = member.ldb.email || "";
            if (name) memberIdentifiers.push(name);
            if (email) memberIdentifiers.push(email);
        } else if ('guest' in member && member.guest) {
            // SpaceGuest with guest data
            const guest = member.guest;
            const name = guest.firstName && guest.lastName 
                ? `${guest.firstName} ${guest.lastName}`.trim()
                : guest.firstName || guest.lastName || "";
            const email = guest.email || "";
            if (name) memberIdentifiers.push(name);
            if (email) memberIdentifiers.push(email);
        }
    });

    // Add Synth to the list
    memberIdentifiers.push("Synth");

    // Sort by length (longest first) to match longer names/emails before shorter ones
    memberIdentifiers.sort((a, b) => b.length - a.length);

    const parts: React.ReactNode[] = [];
    let remainingContent = cleanedContent;
    let currentIndex = 0;

    // First try to find mentions using actual member names
    let foundMention = false;
    
    for (const identifier of memberIdentifiers) {
        // Escape special regex characters in the identifier (name or email)
        const escapedIdentifier = identifier.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        
        // Create regex pattern that matches the mention with word boundaries
        // Looks for @name or @email that is either at start, after whitespace, and followed by whitespace, punctuation, or end
        const regex = new RegExp(`(^|\\s)(@${escapedIdentifier})(?=\\s|[.,!?;:]|$)`, 'g');
        
        let match;
        while ((match = regex.exec(remainingContent)) !== null) {
            const fullMatch = match[0];
            const beforeText = match[1]; // Could be empty string or whitespace
            const mentionText = match[2]; // The @mention part
            const matchIndex = match.index;
            
            // Add text before the mention
            if (matchIndex > 0) {
                parts.push(remainingContent.slice(0, matchIndex));
            }
            
            // Add any whitespace before the mention
            if (beforeText) {
                parts.push(beforeText);
            }

            // Determine color based on identifier type
            let color = "#5452BF"; // Default blue for users
            if (identifier.toLowerCase() === "synth") {
                color = "#56B648"; // Green for Synth
            }

            // Add the colored mention
            parts.push(
                React.createElement(
                    'span',
                    {
                        key: `mention-${currentIndex}-${identifier}`,
                        className: identifier.toLowerCase() === "synth"
                            ? 'font-medium cursor-pointer hover:opacity-80 transition-opacity duration-200'
                            : 'font-medium cursor-pointer hover:underline',
                        style: { color },
                        title: mentionText,
                    },
                    mentionText
                )
            );

            // Update remaining content and continue
            remainingContent = remainingContent.slice(matchIndex + fullMatch.length);
            currentIndex++;
            foundMention = true;
            break;
        }
        
        if (foundMention) {
            // If we found a mention, recursively parse the remaining content
            if (remainingContent.length > 0) {
                const moreParts = parseMentions(remainingContent, spaceMembers);
                parts.push(...moreParts);
            }
            break;
        }
    }

    // If no known member mentions found, check for any email pattern
    if (!foundMention) {
        const emailMentionRegex = /(^|\s)(@[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(?=\s|[.,!?;:]|$)/g;
        let lastIndex = 0;
        let emailMatch;

        while ((emailMatch = emailMentionRegex.exec(cleanedContent)) !== null) {
            const fullMatch = emailMatch[0];
            const beforeText = emailMatch[1]; // Space or start of string
            const mentionText = emailMatch[2]; // @email
            const matchIndex = emailMatch.index;

            // Add text before the match
            if (matchIndex > lastIndex) {
                parts.push(cleanedContent.slice(lastIndex, matchIndex));
            }

            // Add the space if it exists
            if (beforeText) {
                parts.push(beforeText);
            }

            // Add colored email mention (blue for unknown emails)
            parts.push(
                React.createElement(
                    'span',
                    {
                        key: `email-mention-${emailMatch.index}`,
                        className: 'font-medium cursor-pointer hover:underline',
                        style: { color: "#5452BF" },
                        title: mentionText,
                    },
                    mentionText
                )
            );

            lastIndex = emailMatch.index + fullMatch.length;
            foundMention = true;
        }

        // Add remaining text after email mentions
        if (foundMention && lastIndex < cleanedContent.length) {
            parts.push(cleanedContent.slice(lastIndex));
        }
    }

    // ALWAYS check for product references, regardless of whether @mentions were found
    // If we found @mentions, we need to re-parse the entire content to handle both @mentions and products
    if (foundMention) {
        return parseContentWithBothMentionsAndProducts(cleanedContent, spaceMembers);
    }
    
    // If no @mentions found, check for product references in the original content
    const productRegex = /\/([^\/\s]+(?:\s+[^\/\s]+)*)\s+SKU:([A-Za-z0-9\-_]+)(?=\s|[.,!?;:]|$)/g;
    let lastIndex = 0;
    let productMatch;

    while ((productMatch = productRegex.exec(cleanedContent)) !== null) {
        // Add text before the product reference
        if (productMatch.index > lastIndex) {
            parts.push(cleanedContent.slice(lastIndex, productMatch.index));
        }

        const productName = productMatch[1];
        const sku = productMatch[2];
        
        const displayText = `/${productName} SKU:${sku}`;
        const titleText = `Product: ${productName} (SKU: ${sku})`;

        // Add colored product reference
        parts.push(
            React.createElement(
                'span',
                {
                    key: `product-${productMatch.index}`,
                    className: 'font-medium cursor-pointer hover:underline',
                    style: { color: "#F59E0B" }, // Amber color for products
                    title: titleText,
                },
                displayText
            )
        );

        lastIndex = productMatch.index + productMatch[0].length;
    }

    // Add remaining text
    if (lastIndex < cleanedContent.length) {
        parts.push(cleanedContent.slice(lastIndex));
    }

    // If no matches found at all, return original content
    if (parts.length === 0) {
        parts.push(cleanedContent);
    }

    return parts.length > 0 ? parts : [cleanedContent];
};

/**
 * Parses content that contains both @mentions and /product references
 * This function handles both types of highlights simultaneously
 */
const parseContentWithBothMentionsAndProducts = (
    content: string,
    spaceMembers: (SpaceMember | SpaceGuest)[] = []
): React.ReactNode[] => {
    
    const parts: React.ReactNode[] = [];
    let remainingContent = content;
    let currentIndex = 0;
    
    // Create a combined regex that matches both @mentions and /products
    // This regex will find all matches and we'll sort them by position
    const allMatches: Array<{
        index: number;
        length: number;
        type: 'mention' | 'product';
        text: string;
        data: any;
    }> = [];
    
    // Find all @mentions (including emails)
    const memberIdentifiers: string[] = [];
    spaceMembers.forEach(member => {
        if ('ldb' in member && member.ldb) {
            const name = member.ldb.fullName || member.ldb.firstName || "";
            const email = member.ldb.email || "";
            if (name) memberIdentifiers.push(name);
            if (email) memberIdentifiers.push(email);
        } else if ('guest' in member && member.guest) {
            const guest = member.guest;
            const name = guest.firstName && guest.lastName 
                ? `${guest.firstName} ${guest.lastName}`.trim()
                : guest.firstName || guest.lastName || "";
            const email = guest.email || "";
            if (name) memberIdentifiers.push(name);
            if (email) memberIdentifiers.push(email);
        }
    });
    memberIdentifiers.push("Synth");
    
    // Find @mentions
    for (const identifier of memberIdentifiers) {
        const escapedIdentifier = identifier.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(^|\\s)(@${escapedIdentifier})(?=\\s|[.,!?;:]|$)`, 'g');
        
        let match;
        while ((match = regex.exec(content)) !== null) {
            const beforeText = match[1];
            const mentionText = match[2];
            allMatches.push({
                index: match.index + beforeText.length, // Start of @mention, not including space
                length: mentionText.length,
                type: 'mention',
                text: mentionText,
                data: { identifier, beforeText }
            });
        }
    }
    
    // Find email mentions not covered by member identifiers
    const emailMentionRegex = /(^|\s)(@[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(?=\s|[.,!?;:]|$)/g;
    let emailMatch;
    while ((emailMatch = emailMentionRegex.exec(content)) !== null) {
        const beforeText = emailMatch[1];
        const mentionText = emailMatch[2];
        // Only add if not already covered by member identifiers
        const alreadyExists = allMatches.some(m => 
            m.index === emailMatch.index + beforeText.length && m.text === mentionText
        );
        if (!alreadyExists) {
            allMatches.push({
                index: emailMatch.index + beforeText.length,
                length: mentionText.length,
                type: 'mention',
                text: mentionText,
                data: { identifier: mentionText.substring(1), beforeText }
            });
        }
    }
    
    // Find /product references
    const productRegex = /\/([^\/\s]+(?:\s+[^\/\s]+)*)\s+SKU:([A-Za-z0-9\-_]+)(?=\s|[.,!?;:]|$)/g;
    let productMatch;
    while ((productMatch = productRegex.exec(content)) !== null) {
        const productName = productMatch[1];
        const sku = productMatch[2];
        const displayText = `/${productName} SKU:${sku}`;
        
        allMatches.push({
            index: productMatch.index,
            length: productMatch[0].length,
            type: 'product',
            text: displayText,
            data: { productName, sku }
        });
    }
    
    // Sort all matches by position
    allMatches.sort((a, b) => a.index - b.index);
    
    let lastIndex = 0;
    
    // Process all matches in order
    allMatches.forEach((match, idx) => {
        // Add text before this match
        if (match.index > lastIndex) {
            parts.push(content.slice(lastIndex, match.index));
        }
        
        if (match.type === 'mention') {
            // Add space before mention if it exists
            if (match.data.beforeText) {
                parts.push(match.data.beforeText);
            }
            
            let color = "#5452BF"; // Default blue for users
            if (match.data.identifier.toLowerCase() === "synth") {
                color = "#56B648"; // Green for Synth
            }
            
            parts.push(
                React.createElement(
                    'span',
                    {
                        key: `mention-${idx}`,
                        className: match.data.identifier.toLowerCase() === "synth"
                            ? 'font-medium cursor-pointer hover:opacity-80 transition-opacity duration-200'
                            : 'font-medium cursor-pointer hover:underline',
                        style: { color },
                        title: match.text,
                    },
                    match.text
                )
            );
        } else if (match.type === 'product') {
            const titleText = `Product: ${match.data.productName} (SKU: ${match.data.sku})`;
            
            parts.push(
                React.createElement(
                    'span',
                    {
                        key: `product-${idx}`,
                        className: 'font-medium cursor-pointer hover:underline',
                        style: { color: "#F59E0B" }, // Amber color for products
                        title: titleText,
                    },
                    match.text
                )
            );
        }
        
        lastIndex = match.index + match.length;
    });
    
    // Add remaining text
    if (lastIndex < content.length) {
        parts.push(content.slice(lastIndex));
    }
    
    return parts;
};

/**
 * Checks if a string contains any @mentions
 * @param content - The content to check
 * @returns boolean indicating if mentions are present
 */
export const hasMentions = (content: string | null | undefined): boolean => {
    if (!content || typeof content !== 'string') {
        return false;
    }
    // Check for @mentions (names and emails) and /product references
    const mentionRegex = /@([A-Za-z][A-Za-z0-9\-_.]*(?:\s+[A-Za-z][A-Za-z0-9\-_.]+)*?)(?=\s|[.,!?;:]|$)/g;
    const emailMentionRegex = /@[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
    const productRegex = /\/([^\/\s]+(?:\s+[^\/\s]+)*)\s+SKU:([A-Za-z0-9\-_]+)(?=\s|[.,!?;:]|$)/g;
    return mentionRegex.test(content) || emailMentionRegex.test(content) || productRegex.test(content);
};

/**
 * Extracts all mentions from content
 * @param content - The content to extract mentions from
 * @returns Array of mention names (without @)
 */
export const extractMentions = (content: string | null | undefined): string[] => {
    if (!content || typeof content !== 'string') {
        return [];
    }
    
    const mentions: string[] = [];
    
    // Extract @mentions (names) - simple regex extraction
    const mentionRegex = /@([A-Za-z][A-Za-z0-9\-_.]*(?:\s+[A-Za-z][A-Za-z0-9\-_.]+)*?)(?=\s|[.,!?;:]|$)/g;
    let mentionMatch;
    
    while ((mentionMatch = mentionRegex.exec(content)) !== null) {
        mentions.push(mentionMatch[1].trim());
    }
    
    // Extract @email mentions
    const emailMentionRegex = /@([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(?=\s|[.,!?;:]|$)/g;
    let emailMatch;
    
    while ((emailMatch = emailMentionRegex.exec(content)) !== null) {
        mentions.push(emailMatch[1].trim());
    }
    
    // Extract product references
    const productRegex = /\/([^\/\s]+(?:\s+[^\/\s]+)*)\s+SKU:([A-Za-z0-9\-_]+)(?=\s|[.,!?;:]|$)/g;
    let productMatch;
    
    while ((productMatch = productRegex.exec(content)) !== null) {
        // Extract the SKU from the format /productName SKU:sku
        const sku = productMatch[2]; // Group 2 is the SKU
        mentions.push(sku);
    }

    return mentions;
};